import { Modal, notification } from "antd";

/**
 * Turns API / network errors into short messages suitable for end users.
 * Technical SQL, stack, and JSON blobs are never shown as-is.
 */

const GENERIC = "Something went wrong. Please try again in a moment.";
const BUSY = "The system is busy right now. Please wait a moment and try again.";
const TIMEOUT = "The request took too long. Please try again.";
const NETWORK = "Unable to reach the server. Please check your connection and try again.";
const LOAD_CAMPAIGNS =
  "We couldn't load your campaigns right now. Please try again in a moment.";

const looksTechnical = (text) => {
  if (!text || typeof text !== "string") return true;
  const t = text.toLowerCase();
  return (
    t.includes("sql [") ||
    t.includes("hibernate") ||
    t.includes("could not execute statement") ||
    t.includes("lock wait timeout") ||
    t.includes("java.") ||
    t.includes("org.") ||
    t.includes("jakarta.") ||
    t.includes("nullpointerexception") ||
    t.includes("stacktrace") ||
    t.includes("jdbc") ||
    t.includes("mysql") ||
    t.includes("psql") ||
    t.includes("ora-") ||
    t.trim().startsWith("{") ||
    t.length > 220
  );
};

const mapTechnicalToFriendly = (text) => {
  const t = (text || "").toLowerCase();
  if (
    t.includes("lock wait timeout") ||
    t.includes("deadlock") ||
    t.includes("could not serialize")
  ) {
    return BUSY;
  }
  if (t.includes("timeout") || t.includes("timed out") || t.includes("econnaborted")) {
    return TIMEOUT;
  }
  if (
    t.includes("network") ||
    t.includes("connection refused") ||
    t.includes("econnrefused") ||
    t.includes("communications link")
  ) {
    return NETWORK;
  }
  return GENERIC;
};

const extractResponseData = (errorOrPayload) => {
  if (errorOrPayload?.response?.data != null) {
    return errorOrPayload.response.data;
  }
  if (
    errorOrPayload &&
    typeof errorOrPayload === "object" &&
    !Array.isArray(errorOrPayload) &&
    ("message" in errorOrPayload || "error" in errorOrPayload || "fieldErrors" in errorOrPayload)
  ) {
    return errorOrPayload;
  }
  return null;
};

const extractRawText = (errorOrPayload) => {
  if (errorOrPayload == null) return "";
  if (typeof errorOrPayload === "string") return errorOrPayload;

  const data = extractResponseData(errorOrPayload);
  if (data != null) {
    if (typeof data === "string") return data;
    if (typeof data.message === "string" && data.message.trim()) return data.message;
    if (typeof data.error === "string" && data.error.trim()) return data.error;
    if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
    if (data.fieldErrors && typeof data.fieldErrors === "object") {
      const firstFieldError = Object.values(data.fieldErrors).find(
        (value) => typeof value === "string" && value.trim()
      );
      if (firstFieldError) return firstFieldError;
    }
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const first = data.errors.find((item) => typeof item === "string" && item.trim());
      if (first) return first;
    }
  }

  if (typeof errorOrPayload.message === "string") return errorOrPayload.message;
  if (typeof errorOrPayload.error === "string") return errorOrPayload.error;
  return "";
};

/** Prefer the API envelope message when present (e.g. ApiResponse.message). */
export const extractApiErrorMessage = (errorOrPayload, options = {}) => {
  const data = extractResponseData(errorOrPayload);
  if (data != null && typeof data !== "string") {
    if (typeof data.message === "string" && data.message.trim()) {
      const message = data.message.trim();
      if (!looksTechnical(message)) {
        return message;
      }
    }
    if (data.fieldErrors && typeof data.fieldErrors === "object") {
      const firstFieldError = Object.values(data.fieldErrors).find(
        (value) => typeof value === "string" && value.trim() && !looksTechnical(value)
      );
      if (firstFieldError) return firstFieldError.trim();
    }
    if (typeof data.error === "string" && data.error.trim() && !looksTechnical(data.error)) {
      return data.error.trim();
    }
  }

  return getUserFriendlyErrorMessage(errorOrPayload, options);
};

/**
 * @param {unknown} errorOrPayload axios error, response body, or string
 * @param {{ fallback?: string, notFound?: string }} [options]
 * @returns {string}
 */
export const getUserFriendlyErrorMessage = (errorOrPayload, options = {}) => {
  const fallback = options.fallback || GENERIC;
  const code = errorOrPayload?.code;
  const status = errorOrPayload?.response?.status;

  if (code === "ECONNABORTED" || code === "ETIMEDOUT") {
    return TIMEOUT;
  }
  if (!errorOrPayload?.response && errorOrPayload?.request) {
    return NETWORK;
  }
  if (status === 401 || status === 403) {
    return "Your session has expired. Please sign in again.";
  }
  if (status === 404) {
    return options.notFound || "We couldn't find what you were looking for.";
  }

  const raw = extractRawText(errorOrPayload);
  if (!raw || looksTechnical(raw)) {
    const mapped = mapTechnicalToFriendly(raw);
    return mapped === GENERIC ? fallback : mapped;
  }
  return raw.trim();
};

export const showApiError = (error, options = {}) => {
  const {
    title = "Error",
    fallback,
    mode = "notification",
    placement = "topRight",
    duration = 2,
  } = options;

  const description = extractApiErrorMessage(error, { fallback });

  if (mode === "modal") {
    Modal.error({
      title,
      content: description,
      centered: true,
    });
    return;
  }

  notification.error({
    message: title,
    description,
    placement,
    duration,
  });
};

export const FRIENDLY_ERRORS = {
  GENERIC,
  BUSY,
  TIMEOUT,
  NETWORK,
  LOAD_CAMPAIGNS,
};

export default getUserFriendlyErrorMessage;
